import { api } from '../../lib/api.ts';
import type { Body } from '../../lib/body.ts';

export type LetterheadSlot = 'header' | 'footer';

export interface LetterheadPublic {
  hasHeaderImage: boolean;
  hasFooterImage: boolean;
  footerText: string | null;
}

export interface OrgInfo {
  id: string;
  name: string;
  contactEmail: string;
  presidentOnLeave: boolean;
  termNumber: number | null;
  agendaNumberFormat: string | null;
  agendaNumberNext: number;
  yaumiyyaNumberFormat: string | null;
  yaumiyyaNumberNext: number;
  ninmunNumberFormat: string | null;
  ninmunNumberNext: number;
  meetingCadence: 'WEEKLY' | 'MONTHLY' | null;
  // Fixed minimum number of voting councilors for a valid meeting; null = not set.
  quorum: number | null;
  letterhead: LetterheadPublic;
}

// Chair delegation — one row per elected member in succession order.
export interface SuccessionRow {
  id: string;
  name: string;
  nameDv: string | null;
  role: string;
  onLeave: boolean;
  isActingChair: boolean;
  isHead: boolean; // president / VP — fixed at the top, not reorderable
}
export interface Succession {
  actingChairId: string | null;
  rows: SuccessionRow[];
}

export const orgSettingsApi = {
  // Per-body settings (number formats + term) come from the selected body's
  // BodySettings row; omit `body` (or pass COUNCIL) for the council values.
  get: (body?: Body) =>
    api.get<{ org: OrgInfo }>('/org', { params: body ? { body } : undefined }).then((r) => r.data.org),
  // `body` (default COUNCIL on the server) selects which body's number-format /
  // term row the per-body keys are written to. Org-level keys (footerText,
  // meetingCadence, quorum) ignore it.
  patch: (patch: { body?: Body; footerText?: string; termNumber?: number | null; agendaNumberFormat?: string | null; agendaNumberNext?: number; yaumiyyaNumberFormat?: string | null; yaumiyyaNumberNext?: number; ninmunNumberFormat?: string | null; ninmunNumberNext?: number; meetingCadence?: 'WEEKLY' | 'MONTHLY' | null; quorum?: number | null }) =>
    api.patch<{ letterhead: LetterheadPublic; org: OrgInfo }>('/org', patch).then((r) => r.data.letterhead),
  // Letterhead images are per body — `body` (default COUNCIL on the server)
  // selects which body's header/footer slot the call reads or writes.
  uploadImage: (slot: LetterheadSlot, file: File, body?: Body) => {
    const fd = new FormData();
    fd.append('image', file);
    return api
      .post<{ letterhead: LetterheadPublic }>(`/org/letterhead/${slot}`, fd, { params: body ? { body } : undefined })
      .then((r) => r.data.letterhead);
  },
  // President's "on leave" delegation toggle (only the body's president may call
  // it). The server derives the body from the caller's role; `body` is only used
  // here to read/refresh the matching BodySettings row via `get(body)`.
  setPresidentLeave: (onLeave: boolean) =>
    api.post<{ presidentOnLeave: boolean }>('/org/president-leave', { onLeave }).then((r) => r.data.presidentOnLeave),
  // Chair delegation / succession for a body (admin or the body's president).
  succession: (body: Body) =>
    api.get<Succession>('/org/chair-succession', { params: { body } }).then((r) => r.data),
  setChairLeave: (body: Body, userId: string, onLeave: boolean) =>
    api.post<Succession>('/org/chair-leave', { body, userId, onLeave }).then((r) => r.data),
  setChairOrder: (body: Body, memberIds: string[]) =>
    api.post<Succession>('/org/chair-order', { body, memberIds }).then((r) => r.data),
  removeImage: (slot: LetterheadSlot, body?: Body) =>
    api.delete(`/org/letterhead/${slot}`, { params: body ? { body } : undefined }),
  imageUrl: (slot: LetterheadSlot) => `/api/org/letterhead/${slot}/file`,
  // Image previews need the auth header, so fetch as a blob via the api
  // instance and hand back an object URL. The caller must revoke it.
  fetchImageBlobUrl: (slot: LetterheadSlot, body?: Body) =>
    api
      .get(`/org/letterhead/${slot}/file`, { responseType: 'blob', params: body ? { body } : undefined })
      .then((r) => URL.createObjectURL(r.data as Blob)),
};
